import { useEffect, useRef } from "react";
import { useU2000WebSocketContext } from "../../../../../u2000Core/context/websocket/U2000WebSocketContext";
import { isNotificationMessage, U3141_MERGE_ANNOTATED_LOT_CHAIN_INDEX_PDFS_JOB_TYPE } from "../../../../../u2000Core/types/U2000WebSocketMessages";

const U3141_ANNOTATED_LOT_CHAIN_JOB_TYPES = [U3141_MERGE_ANNOTATED_LOT_CHAIN_INDEX_PDFS_JOB_TYPE];

const U3141_ANNOTATED_CHAIN_DEDUPE_MAX = 200;

export type U3141AnnotatedLotChainNotificationPayload = {
  masterLotChainId: string;
  failed?: boolean;
  message?: string;
};

function u3141AnnotatedChainDedupeKey(message: { id: string }, metadata: Record<string, unknown> | undefined): string {
  const jobIdRaw = metadata?.jobId;
  const jobId = typeof jobIdRaw === "string" ? jobIdRaw.trim() : "";
  if (jobId !== "") {
    return `job:${jobId}`;
  }
  return `nid:${message.id}`;
}

/**
 * Écoute les notifications WebSocket "notification:new" pour le job U3141 fusion chaîne annotée.
 * Lorsque metadata.jobType === U3141_MERGE_ANNOTATED_LOT_CHAIN_INDEX_PDFS
 * et metadata.masterLotChainId correspond au lot maître courant, appelle onCompleted.
 *
 * Déduplication : évite plusieurs enchaînements refetch + lotChainGraph si le serveur émet
 * deux fois la même fin de job (même `metadata.jobId` ou même `message.id` de notification).
 */
export function useU3141AnnotatedLotChainNotification(
  masterLotChainId: string | undefined,
  onCompleted: (payload: U3141AnnotatedLotChainNotificationPayload) => void
) {
  const { on, off } = useU2000WebSocketContext();
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const processedDedupeKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!masterLotChainId) return;

    const handleNotification = (message: unknown) => {
      if (!isNotificationMessage(message)) return;

      const metadata = message.metadata as Record<string, unknown> | undefined;
      const jobType = metadata?.jobType as string | undefined;
      const masterLotChainIdFromMsg = metadata?.masterLotChainId as string | undefined;

      if (
        typeof jobType !== "string" ||
        !U3141_ANNOTATED_LOT_CHAIN_JOB_TYPES.includes(jobType) ||
        masterLotChainIdFromMsg !== masterLotChainId
      ) {
        return;
      }

      const dedupeKey = u3141AnnotatedChainDedupeKey(message, metadata);
      if (processedDedupeKeysRef.current.has(dedupeKey)) {
        if (import.meta.env.DEV) {
          console.log("[U3141 Chaîne annotée] Notification ignorée (doublon):", dedupeKey);
        }
        return;
      }
      processedDedupeKeysRef.current.add(dedupeKey);
      if (processedDedupeKeysRef.current.size > U3141_ANNOTATED_CHAIN_DEDUPE_MAX) {
        processedDedupeKeysRef.current.clear();
      }

      if (import.meta.env.DEV) {
        console.log("[U3141 Chaîne annotée] Notification reçue, refetch masterLotChainId:", masterLotChainId, dedupeKey);
      }
      onCompletedRef.current({
        masterLotChainId,
        failed: message.importance === "error" || message.importance === "critical",
        message: message.message,
      });
    };

    on("notification:new", handleNotification);
    return () => off("notification:new", handleNotification);
  }, [masterLotChainId, on, off]);
}
